import React, { useState, useEffect, useRef } from "react";
import { motion, useMotionValue, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";

// react-snap utilise le user-agent "ReactSnap" pendant le pré-rendu : on désactive
// alors les états initiaux à opacity 0 pour que le HTML capturé soit pleinement
// visible. Les animations des vrais visiteurs ne sont pas affectées.
const IS_PRERENDER = typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap");

const USE_CASES = [
  {
    title: "Surveillance de respect de consignes",
    detail: "Un engin ou un opérateur entrant dans une zone protégée, dangereuse ou interdite."
  },
  {
    title: "Respect des distances de sécurité et des hauteurs",
    detail: "Respecter un écart suffisant vis-à-vis des fouilles, ouvrages, infrastructures et équipements sensibles (groupes électrogènes, transformateurs, lignes aériennes, etc.) pour prévenir tout risque électrique, de heurt, de chute, d'effondrement ou de collision."
  },
  {
    title: "Creusement en zone interdite",
    detail: "Empêcher les engins de creuser au-dessus d'un réseau (électrique, gazier, hydraulique…)."
  },
  {
    title: "Présence en front de taille",
    detail: "Un opérateur ou un piéton se trouve au bord non protégé d'une fouille ou d'un talus, zone à risque d'effondrement ou de chute."
  },
  {
    title: "Arrêt machine automatique",
    detail: "La machine est stoppée en cas d'intrusion humaine dans sa zone de danger."
  },
  {
    title: "Co-activité engins/piétons",
    detail: "Détection des situations où engins et piétons évoluent à proximité les uns des autres."
  },
  {
    title: "Pratiques dangereuses en zones consignées",
    detail: "Pas de soudure dans une zone soumise à permis de feu ou de travaux par points chauds."
  },
  {
    title: "Non-port des équipements de protection individuelle (EPI)",
    detail: "La caméra identifie automatiquement l'absence de casque, gilet ou autres EPI."
  },
  {
    title: "Écoulements non contrôlés",
    detail: "Repérage des fuites et déversements dès leur apparition."
  },
  {
    title: "Stationnements et dépôts gênants ou dangereux",
    detail: "Un véhicule, un engin ou des matériaux immobilisés ou stockés dans une zone de passage critique ou interdite."
  },
  {
    title: "Présence en zone de levage",
    detail: "Un opérateur entre dans la zone d'action d'une grue ou d'un appareil de levage."
  },
  {
    title: "Objets non autorisés sur tapis roulant",
    detail: "Détection d'éléments étrangers sur une ligne de convoyage."
  },
  {
    title: "Emploi d'outils interdits",
    detail: "Un opérateur utilise un marteau/burin pour un désenrobage."
  },
  {
    title: "Comptage et classification",
    detail: "Comptage et identification des véhicules, personnes, animaux, etc."
  }
];

const USE_CASES_INTRO = "Notre approche est celle de la prévention : repérer les situations à risque avant qu'elles ne deviennent des accidents, pour protéger les équipes et les infrastructures. Le système couvre notamment :";

export default function LandingPage() {
  const [activeSection, setActiveSection] = useState("Notre système"); // Section active par défaut
  const [navFontSize, setNavFontSize] = useState(14); // Taille de police responsive
  const [navPadding, setNavPadding] = useState('8px 12px'); // Padding responsive
  const [navGap, setNavGap] = useState('12px'); // Gap responsive
  const [heroTitleSize, setHeroTitleSize] = useState('text-2xl'); // Taille du titre hero responsive
  const [heroBulletSize, setHeroBulletSize] = useState('text-sm'); // Taille des bullet points responsive
  const [blockTitleSize, setBlockTitleSize] = useState('text-lg'); // Taille des titres de blocs responsive
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [isInForbiddenZone, setIsInForbiddenZone] = useState(false);
  
  // Ordre des sections pour la navigation par swipe
  const sectionsOrder = ["Notre système", "Notre objectif", "Notre entreprise", "Nous contacter", "FAQ"];
  
  // Refs pour stocker les positions de touch
  const touchStartXRef = useRef(null);
  const touchEndXRef = useRef(null);
  
  // Ajustement de la taille de police responsive et espacement
  useEffect(() => {
    const adjustNav = () => {
      const screenWidth = window.innerWidth;
      let newFontSize = 15;
      let newPadding = '8px 12px';
      let newGap = '12px';
      let newHeroTitleSize = 'text-2xl';
      let newHeroBulletSize = 'text-sm';
      let newBlockTitleSize = 'text-lg';
      
      // Breakpoints plus fins pour les très petits écrans
      if (screenWidth < 320) {
        newFontSize = 8;
        newPadding = '4px 6px';
        newGap = '2px';
        newHeroTitleSize = 'text-xl';
        newHeroBulletSize = 'text-sm';
        newBlockTitleSize = 'text-sm';
      } else if (screenWidth < 360) {
        newFontSize = 9;
        newPadding = '5px 7px';
        newGap = '3px';
        newHeroTitleSize = 'text-2xl';
        newHeroBulletSize = 'text-sm';
        newBlockTitleSize = 'text-base';
      } else if (screenWidth < 400) {
        newFontSize = 9.5;
        newPadding = '5px 7px';
        newGap = '3px';
        newHeroTitleSize = 'text-2xl';
        newHeroBulletSize = 'text-sm';
        newBlockTitleSize = 'text-base';
      } else if (screenWidth < 480) {
        newFontSize = 10;
        newPadding = '6px 8px';
        newGap = '4px';
        newHeroTitleSize = 'text-3xl';
        newHeroBulletSize = 'text-base';
        newBlockTitleSize = 'text-lg';
      } else if (screenWidth < 640) {
        newFontSize = 10.5;
        newPadding = '6px 8px';
        newGap = '4px';
        newHeroTitleSize = 'text-3xl';
        newHeroBulletSize = 'text-base';
        newBlockTitleSize = 'text-lg';
      } else if (screenWidth < 768) {
        newFontSize = 12;
        newPadding = '7px 10px';
        newGap = '8px';
        newHeroTitleSize = 'text-4xl';
        newHeroBulletSize = 'text-lg';
        newBlockTitleSize = 'text-xl';
      } else if (screenWidth < 1024) {
        newFontSize = 13;
        newPadding = '8px 12px';
        newGap = '12px';
        newHeroTitleSize = 'text-5xl';
        newHeroBulletSize = 'text-xl';
        newBlockTitleSize = 'text-2xl';
      } else if (screenWidth < 1280) {
        newFontSize = 14;
        newPadding = '8px 12px';
        newGap = '12px';
        newHeroTitleSize = 'text-5xl';
        newHeroBulletSize = 'text-xl';
        newBlockTitleSize = 'text-2xl';
      } else {
        newFontSize = 15;
        newPadding = '8px 12px';
        newGap = '12px';
        newHeroTitleSize = 'text-5xl';
        newHeroBulletSize = 'text-xl';
        newBlockTitleSize = 'text-2xl';
      }
      
      setNavFontSize(newFontSize);
      setNavPadding(newPadding);
      setNavGap(newGap);
      setHeroTitleSize(newHeroTitleSize);
      setHeroBulletSize(newHeroBulletSize);
      setBlockTitleSize(newBlockTitleSize);
    };

    adjustNav();
    window.addEventListener('resize', adjustNav);
    
    return () => {
      window.removeEventListener('resize', adjustNav);
    };
  }, []);

  const scrollToForm = () => {
    setActiveSection("Nous contacter");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    // Configuration EmailJS
    const SERVICE_ID = "service_piqjyqb";
    const TEMPLATE_ID = "template_jzc0gss";
    const PUBLIC_KEY = "rd1SXp_QzfwT119om";

    try {
      const result = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_email: "theo.gillet@iaegis.fr"
        },
        PUBLIC_KEY
      );

      console.log("Email envoyé avec succès:", result);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Détection de collision
  const excavatorX = useMotionValue(-100);
  
  const checkCollision = (x) => {
    excavatorX.set(x);
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 2000;
    const zoneLeft = screenWidth / 2 - 100; // Centre - moitié de la largeur (200px)
    const zoneRight = screenWidth / 2 + 100;
    const excavatorLeft = x;
    const excavatorRight = x + 80; // Largeur de la pelleteuse
    
    // Vérifier si les bounding boxes se croisent
    const collision = excavatorRight > zoneLeft && excavatorLeft < zoneRight;
    setIsInForbiddenZone(collision);
  };

  // Fonction pour rendre le contenu de la section "Notre système"
  const renderSystemSection = () => (
    <>
      {/* HERO SECTION */}
      <section className="flex flex-col lg:flex-row items-center justify-between px-8 md:px-20 py-20 flex-grow gap-8" style={{ 
        backgroundColor: '#FFFFFF',
        backgroundImage: `url(${process.env.PUBLIC_URL}/Chantier.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative'
      }}>
        {/* Overlay de transparence */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.75)',
          zIndex: 0
        }}></div>
        
        <motion.div
          className="w-full lg:w-2/3 lg:max-w-[60%] space-y-4"
          initial={IS_PRERENDER ? false : { opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <h1 className={`${heroTitleSize} font-bold leading-tight`} style={{ color: '#022444' }}>
            Une caméra de détection intelligente pour vos chantiers
          </h1>
          <p className={`${heroBulletSize} text-gray-700`} style={{ textAlign: 'justify' }}>
            IAEGIS est une start-up française spécialisée dans le traitement d'images par IA.
            <br />
            Nous développons un logiciel d'anticipation des accidents sur chantiers
            et sites industriels, déployé via nos caméras ou via nos boîtiers raccordés à vos
            caméras existantes. 
            <br />
            Alertes en temps réel : SMS, email, alerte sonore ou lumineuse,
            jusqu'à l'arrêt machine automatique.
          </p>
          <ul className={`${heroBulletSize} text-gray-700 space-y-2`} style={{ listStyleType: 'disc', paddingLeft: '20px', textAlign: 'justify' }}>
            <li>Détections et alertes en temps réel</li>
            <li>Logiciel sur-mesure pour être adapté à vos besoins</li>
            <li>Facile d'installation et intuitif d'utilisation</li>
            <li>IP65+ et conforme RGPD</li>
          </ul>
          <button
            onClick={scrollToForm}
            className="mt-6 px-6 py-3 rounded-xl font-semibold text-lg transition-all text-white hover:opacity-90"
            style={{ backgroundColor: '#1A79B6' }}
          >
            Demander une démonstration
          </button>
        </motion.div>

        <div className="lg:w-1/3 flex justify-center mt-12 lg:mt-0" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            style={{ position: 'relative', marginLeft: '-6%' }}
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/Camera.png`}
              alt="Caméra de détection IARGOS d'IAEGIS"
              className="w-80 md:w-[420px] drop-shadow-2xl"
              style={{
                border: 'none',
                outline: 'none',
                boxShadow: 'none'
              }}
            />
            <img
              src={`${process.env.PUBLIC_URL}/Boitier.png`}
              alt="Boîtier blanc IAEGIS pour raccorder des caméras existantes"
              className="w-80 md:w-[420px] drop-shadow-2xl"
              style={{
                position: 'absolute',
                right: '-28%',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.2))'
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* SECTION IARGOS - 4 colonnes */}
      <section className="pt-16 px-8 md:px-20" style={{ backgroundColor: '#FFFFFF', paddingBottom: 0, marginBottom: 0 }}>
        <div className="max-w-7xl mx-auto" style={{ marginBottom: 0 }}>
          <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center" style={{ color: '#022444' }}>
            Notre solution : IARGOS
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{ marginBottom: 0 }}>
            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3 text-center" style={{ color: '#022444' }}>
                Un logiciel d'anticipation des accidents
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm" style={{ textAlign: 'justify' }}>
                IARGOS est un logiciel d'anticipation des accidents pour chantiers et sites industriels. 
                Il se déploie soit dans notre caméra autonome, soit dans un boîtier connecté à l'une de vos 
                caméras existantes, et déclenche des actions correctives en temps réel dès qu'une situation 
                à risque est détectée.
              </p>
          </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3 text-center" style={{ color: '#022444' }}>
                Accessible et pilotable à distance depuis votre PC, téléphone ou tablette
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm" style={{ textAlign: 'justify' }}>
                Vous pouvez définir des zones d'interdiction spécifiques et paramétrer les comportements à risque à détecter à distance depuis votre PC, téléphone ou tablette.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3 text-center" style={{ color: '#022444' }}>
                Qui alerte en temps réel, jusqu'à l'arrêt machine
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm" style={{ textAlign: 'justify' }}>
                En cas de non-respect d'une consigne, la caméra le signale dès son apparition afin d'éviter l'accident : 
                alerte SMS ou email, alerte sonore ou lumineuse sur site, jusqu'à l'arrêt machine automatique.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3 text-center" style={{ color: '#022444' }}>
                Et qui enregistre automatiquement les incidents
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm" style={{ textAlign: 'justify' }}>
                Tous les événements sont consignés et mis en forme automatiquement dans un dashboard accessible à distance, permettant une bonne lisibilité des données et
                assurant une traçabilité fiable et continue tout en respectant les normes RGPD.
              </p>
          </div>
          </div>
        </div>
      </section>

      {/* TITRE SON FONCTIONNEMENT */}
      <section className="py-16 px-8 md:px-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold mb-0 text-center" style={{ color: '#022444' }}>
            Fonctionnement schématisé
          </h2>
        </div>
      </section>

      {/* SECTION ANIMATION + BLOC ACCESSIBLE */}
      <section className="relative w-full" style={{ backgroundColor: '#FFFFFF', marginTop: '-60px', overflow: 'visible' }}>
      {/* BANDE ANIMATION PELLETEUSE */}
      <div className="relative w-full overflow-hidden" style={{ height: '500px', paddingTop: 0 }}>
        {/* Rectangle orange foncé */}
        <div style={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '200px',
          height: '100px',
          backgroundColor: '#FFFFFF',
          border: '3px solid #FF6600'
        }}>
          <div style={{
            position: 'absolute',
            top: '-25px',
            left: '5px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#FF6600'
          }}>
            Zone d'interdiction
          </div>
        </div>

        {/* Rectangle gris foncé avec hachures */}
        <div style={{
          position: 'absolute',
          top: 'calc(40% + 50px)',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '200px',
          height: '60px',
          backgroundColor: 'transparent',
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 8px,
            #888888 8px,
            #888888 9px
          )`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid #333333'
        }}>
          <span style={{
            color: '#333333',
            fontSize: '16px',
            fontWeight: 'bold',
            zIndex: 1
          }}>
            Réseau
          </span>
        </div>
        
        {/* Message d'alerte — jamais rendu pendant le pré-rendu react-snap :
            un nœud conditionnel absent du rendu client initial fausserait l'hydratation */}
        <AnimatePresence>
        {!IS_PRERENDER && isInForbiddenZone && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
             style={{
               position: 'absolute',
               top: 'calc(35% + 125px + 20px)',
               left: 'calc(50% - 150px)',
               width: '300px',
               backgroundColor: '#FF0000',
               color: '#FFFFFF',
               padding: '10px 20px',
               borderRadius: '8px',
               fontSize: '13px',
               fontWeight: 'bold',
               boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
               zIndex: 20,
               whiteSpace: 'nowrap',
               textAlign: 'center',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center'
             }}
          >
          📱  Alerte envoyée au responsable du chantier
          </motion.div>
        )}
        </AnimatePresence>
        
        
        {/* Vue caméra centrée */}
        <div style={{
          position: 'absolute',
          top: '35%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 15,
          minWidth: '300px'
        }}>
          {/* Texte "Vue Caméra" */}
          <div style={{
            position: 'absolute',
            top: '-21px',
            left: '20px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#000000',
            zIndex: 16
          }}>
            Vue Caméra
          </div>
          <img 
            src={`${process.env.PUBLIC_URL}/Vue_Caméra_nobg.png`}
            alt="Vue de la caméra montrant une zone d'interdiction sur un plan de chantier" 
            style={{ 
              width: '300px',
              height: '250px'
            }} 
          />
        </div>

        <motion.div
          className="text-6xl absolute"
          animate={{ x: [-100, typeof window !== 'undefined' ? window.innerWidth + 100 : 2000] }}
          transition={{
            duration: typeof window !== 'undefined' ? (window.innerWidth + 200) / 120 : 18,
            repeat: Infinity,
            ease: "linear"
          }}
          onUpdate={(latest) => {
            if (latest.x !== undefined) {
              checkCollision(latest.x);
            }
          }}
          style={{ 
            display: 'inline-block',
            rotateY: 180,
            top: '34.6%',
            transform: 'translateY(-50%)',
            position: 'relative',
            zIndex: 10
          }}
        >
          <div style={{ position: 'relative' }}>
            <div style={{ 
              position: 'absolute', 
              top: '-20px', 
              left: '6px', 
              fontSize: '14px', 
              fontWeight: 'bold',
              color: isInForbiddenZone ? '#FF0000' : '#00FF00',
              transform: 'scaleX(-1)'
            }}>
              Pelleteuse
            </div>
            <img 
              src={`${process.env.PUBLIC_URL}/excavator-icon.jpg`}
              alt="Icône de pelleteuse" 
              style={{ 
                width: '80px', 
                height: '77px', 
                border: `3px solid ${isInForbiddenZone ? '#FF0000' : '#00FF00'}` 
              }} 
            />
          </div>
        </motion.div>
      </div>

      {/* Blocs Explications et Autres cas d'usage */}
      <div className="px-8 md:px-20 relative" style={{ paddingTop: 0, paddingBottom: '30px', marginTop: '-100px' }}>
        {/* Bloc Explications */}
        <div className="mx-auto mb-8" style={{ maxWidth: '600px' }}>
          <div className="bg-gray-50 p-3 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
            <h3 className="text-xl font-bold mb-1 text-center" style={{ color: '#022444' }}>
              Explications
            </h3>
            <p className="text-gray-700 text-[13px] leading-relaxed" style={{ textAlign: 'justify' }}>
              Dans cet exemple, l'utilisateur a défini une zone d'interdiction des pelleteuses afin qu'elles ne creusent pas au-dessus d'un réseau (électrique, gazier, hydraulique,...).
              <br />
              Lorsque la pelleteuse franchit l'interdiction, il est alors alerté en temps réel par SMS et Email afin de pouvoir prévenir les équipes sur place et éviter l'accident. 
            </p>
          </div>
        </div>

        {/* Bloc Autres cas d'usage */}
        <div className="mx-auto mb-8" style={{ maxWidth: '600px' }}>
          <div className="bg-gray-50 p-3 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
            <h3 className="text-xl font-bold mb-1 text-center" style={{ color: '#022444' }}>
              Exemples d'autres cas d'usage possibles
            </h3>
            <ul className="text-gray-700 text-[13px] leading-relaxed" style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '10px', textAlign: 'justify' }}>
              {USE_CASES.map((useCase) => (
                <li key={useCase.title}>
                  <strong>{useCase.title} :</strong> {useCase.detail}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      </section>
    </>
  );

  // Fonction pour rendre la section "Notre objectif"
  const renderObjectiveSection = () => (
    <section className="py-20 px-8 md:px-20" style={{ backgroundColor: '#FFFFFF', minHeight: '80vh' }}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={IS_PRERENDER ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className={`${heroTitleSize} font-bold mb-12 text-center`} style={{ color: '#022444' }}>
            Repenser le chantier de demain
          </h2>
          
          <div className={`space-y-8 ${heroBulletSize} text-gray-700`}>
            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className={`${blockTitleSize} font-semibold mb-4`} style={{ color: '#022444' }}>
                Protéger les travailleurs et les infrastructures existantes
              </h3>
              <p className="leading-relaxed" style={{ textAlign: 'justify' }}>
                Garantir le respect des consignes de sécurité et détecter en temps réel les comportements à risque permet d'assurer la protection des travailleurs et des infrastructures.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className={`${blockTitleSize} font-semibold mb-4`} style={{ color: '#022444' }}>
                Prévenir les incidents et réduire les coûts
              </h3>
              <p className="leading-relaxed" style={{ textAlign: 'justify' }}>
                Identifier rapidement les écarts de sécurité et les situations dangereuses contribue à protéger les travailleurs sur chantier, éviter les dommages matériels, limiter les arrêts de production et réduire les frais de réparation.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className={`${blockTitleSize} font-semibold mb-4`} style={{ color: '#022444' }}>
                Réduire les déplacements physiques
              </h3>
              <p className="leading-relaxed" style={{ textAlign: 'justify' }}>
                Pouvoir surveiller à distance, c'est aussi limiter les visites de contrôle tout en maintenant un haut niveau de vigilance et de réactivité.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );

  // Fonction pour rendre la section "Notre entreprise"
  const renderCompanySection = () => (
    <section className="py-20 px-8 md:px-20" style={{ backgroundColor: '#FFFFFF', minHeight: '80vh' }}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={IS_PRERENDER ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className={`${heroTitleSize} font-bold mb-8 text-center`} style={{ color: '#022444' }}>
            Notre entreprise
          </h2>
          
          <div className={`space-y-8 ${heroBulletSize} text-gray-700`}>
            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className={`${blockTitleSize} font-semibold mb-4`} style={{ color: '#022444' }}>Qui sommes-nous ?</h3>
              <p className="leading-relaxed" style={{ textAlign: 'justify' }}>
                IAEGIS est une SASU française, créée en juin 2025 et basée à Brest, spécialisée dans le traitement d'images, vidéos et signaux par IA. 
                Nous développons des solutions technologiques sur-mesures entièrement adaptées aux besoins de nos clients. 
                Notre produit principal est un logiciel d'anticipation des accidents, déployé sur chantiers et 
                sites industriels via nos caméras ou nos boîtiers raccordés aux caméras existantes.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className={`${blockTitleSize} font-semibold mb-4`} style={{ color: '#022444' }}>Notre mission</h3>
              <p className="leading-relaxed" style={{ textAlign: 'justify' }}>
                Développer des outils IA sur-mesures de détection performants et fiables pour tous les secteurs d'activités.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl border-2" style={{ borderColor: '#1A79B6' }}>
              <h3 className={`${blockTitleSize} font-semibold mb-4`} style={{ color: '#022444' }}>Nos valeurs</h3>
              <ul className="space-y-3 leading-relaxed" style={{ textAlign: 'justify' }}>
                <li><strong>Fiabilité :</strong> La satisfaction de nos clients et la fiabilité de nos solutions sont nos priorités absolues.</li>
                <li><strong>Innovation continue :</strong> Nous développons constamment de nouvelles fonctionnalités pour répondre aux besoins du secteur.</li>
                <li><strong>Proximité client :</strong> Un accompagnement personnalisé et réactif pour chaque projet.</li>
                <li><strong>Respect de la vie privée :</strong> Conformité RGPD et respect des données personnelles.</li>
              </ul>
            </div>

          </div>
        </motion.div>
      </div>
    </section>
  );

  // Fonction pour rendre la section "FAQ"
  const renderFAQSection = () => (
    <section className="py-20 px-8 md:px-20" style={{ backgroundColor: '#FFFFFF', minHeight: '80vh' }}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={IS_PRERENDER ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className={`${heroTitleSize} font-bold mb-12 text-center`} style={{ color: '#022444' }}>
            Questions fréquentes
          </h2>
          
          <div className="space-y-6">
            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Comment fonctionne le système de détection ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Notre caméra utilise l'intelligence artificielle pour analyser en temps réel les images du chantier. 
                Elle détecte automatiquement les comportements à risque que vous avez préalablement configurés (zones d'interdiction, 
                non-port d'EPI, etc.) et déclenche des actions immédiates : alerte SMS ou email, alerte sonore ou lumineuse sur site, 
                jusqu'à l'arrêt machine automatique.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Quels cas d'usage le système peut-il détecter ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                {USE_CASES_INTRO}
              </p>
              <ul className="text-gray-700 leading-relaxed" style={{ listStyleType: 'disc', paddingLeft: '20px', marginTop: '10px', textAlign: 'justify' }}>
                {USE_CASES.map((useCase) => (
                  <li key={useCase.title}>{useCase.title}</li>
                ))}
              </ul>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Combien de temps prend l'installation terrain ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                L'installation est rapide et simple. La caméra peut être installée en quelques minutes et la configuration 
                des zones d'interdiction se fait en quelques clics.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                La caméra fonctionne-t-elle sans connexion internet sur le chantier ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Oui. La connectivité est hybride : la caméra peut fonctionner en déploiement entièrement local, 
                sans connectivité ni communication avec l'extérieur, utiliser son propre réseau 4G intégré, 
                ou s'appuyer sur le Wi-Fi du site. En mode entièrement local, les alertes sur site 
                (sonores, lumineuses, arrêt machine) restent opérationnelles.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Comment la caméra est-elle alimentée sur un chantier sans électricité ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                L'alimentation est hybride : la caméra fonctionne sur sa propre batterie, avec plus d'une journée 
                d'autonomie, ou reliée au réseau électrique du site. Une alimentation par panneaux solaires est à l'étude.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Peut-on utiliser plusieurs caméras sur un même chantier ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Bien sûr. Plusieurs caméras peuvent couvrir un même chantier et, pour aller plus loin, 
                elles peuvent être connectées entre elles afin d'obtenir une vue 3D de certaines zones.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Puis-je utiliser mes caméras existantes ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Oui. Notre solution est avant tout un logiciel d'anticipation des accidents : il peut être déployé 
                soit via notre caméra autonome, soit via nos boîtiers raccordés à vos caméras existantes.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Le système est-il conforme au RGPD, à la CNIL et à l'AI Act ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Oui, la solution est conçue dès l'origine pour être conforme à ces réglementations (conformité by design). 
                Les images sont traitées localement, les données sont chiffrées et nous ne conservons que les événements 
                nécessaires à la traçabilité. Les éléments identifiés (personnes, plaques d'immatriculation…) 
                peuvent être anonymisés, et vous gardez le contrôle total sur vos données.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Puis-je personnaliser les zones d'interdiction ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Absolument ! Via l'interface de notre application, vous pouvez définir des zones d'interdiction personnalisées, 
                configurer les comportements à risque à détecter, et paramétrer les alertes selon vos besoins spécifiques.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Quel est le délai de mise en service après commande ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Nous procédons actuellement par preuves de concept, d'une durée de 1 à 2 mois, 
                afin de démontrer le bon fonctionnement de notre solution dans votre environnement terrain.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Quel est le coût du système ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Le coût dépend de la taille de votre chantier et de vos besoins spécifiques. 
                Veuillez nous contacter pour une démonstration personnalisée et un devis adapté à votre projet.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Proposez-vous une location ou uniquement la vente ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Nous proposons les deux, en nous adaptant aux besoins de nos clients : la location est généralement 
                plus adaptée aux besoins courts, comme les chantiers de maintenance, et l'achat aux déploiements 
                de longue durée.
              </p>
            </div>

            <div className="border-l-4 pl-6 py-4" style={{ borderColor: '#1A79B6' }}>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#022444' }}>
                Le système fonctionne-t-il par tous les temps ?
              </h3>
              <p className="text-gray-700 leading-relaxed" style={{ textAlign: 'justify' }}>
                Oui, équipée de son boitier IP65, notre caméra est conçue pour fonctionner dans des conditions difficiles (pluie, brouillard, faible luminosité). 
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );

  // Fonctions pour gérer le swipe
  const minSwipeDistance = 50; // Distance minimale pour déclencher un swipe

  const onTouchStart = (e) => {
    touchEndXRef.current = null;
    touchStartXRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndXRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartXRef.current || !touchEndXRef.current) return;
    
    const distance = touchStartXRef.current - touchEndXRef.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = sectionsOrder.indexOf(activeSection);
      let newIndex;

      if (isLeftSwipe) {
        // Swipe vers la gauche = section suivante
        newIndex = currentIndex < sectionsOrder.length - 1 ? currentIndex + 1 : currentIndex;
      } else {
        // Swipe vers la droite = section précédente
        newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
      }

      if (newIndex !== currentIndex) {
        const newSection = sectionsOrder[newIndex];
        setActiveSection(newSection);
        
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    
    // Réinitialiser les valeurs
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  return (
    <div className="min-h-screen flex flex-col text-gray-900" style={{ backgroundColor: '#F5F5F5' }}>
      {/* HEADER LOGO */}
      <header className="w-full flex items-center justify-center px-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '60px', backgroundColor: '#FFFFFF', borderBottom: '1px solid #e5e7eb', zIndex: 100 }}>
        <img src={`${process.env.PUBLIC_URL}/Logo_IAEGIS_complet.png`} alt="Logo IAEGIS" style={{ height: '40px' }} />
      </header>

      {/* HEADER NAVIGATION */}
      <header className="w-full flex items-center justify-center px-2 md:px-8" style={{ position: 'fixed', top: '60px', left: 0, right: 0, height: '60px', backgroundColor: '#FFFFFF', borderBottom: '1px solid #e5e7eb', zIndex: 100 }}>
        {/* Menu de navigation centré */}
        <nav className="flex items-center justify-center" style={{ flexWrap: 'nowrap', gap: navGap, maxWidth: '100%' }}>
          {["Notre système", "Notre objectif", "Notre entreprise", "Nous contacter", "FAQ"].map((section) => (
            <button
              key={section}
              onClick={() => {
                setActiveSection(section);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              style={{
                padding: navPadding,
                fontSize: `${navFontSize}px`,
                fontWeight: activeSection === section ? 'bold' : 'normal',
                color: activeSection === section ? '#1A79B6' : '#022444',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeSection === section ? `2px solid #1A79B6` : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (activeSection !== section) {
                  e.target.style.color = '#1A79B6';
                }
              }}
              onMouseLeave={(e) => {
                if (activeSection !== section) {
                  e.target.style.color = '#022444';
                }
              }}
            >
              {section}
            </button>
          ))}
        </nav>
      </header>

      {/* Contenu selon la section active */}
      <div 
        style={{ paddingTop: '120px' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
      {/* Toutes les sections restent montées dans le DOM (SEO) ; seule la section active est visible */}
        <motion.div
          key="system"
          initial={IS_PRERENDER ? false : { opacity: 0 }}
          animate={{ opacity: activeSection === "Notre système" ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: activeSection === "Notre système" ? 'block' : 'none' }}
        >
          {renderSystemSection()}
        </motion.div>

        <motion.div
          key="objective"
          initial={IS_PRERENDER ? false : { opacity: 0 }}
          animate={{ opacity: activeSection === "Notre objectif" ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: activeSection === "Notre objectif" ? 'block' : 'none' }}
        >
          {renderObjectiveSection()}
        </motion.div>

        <motion.div
          key="company"
          initial={IS_PRERENDER ? false : { opacity: 0 }}
          animate={{ opacity: activeSection === "Notre entreprise" ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: activeSection === "Notre entreprise" ? 'block' : 'none' }}
        >
          {renderCompanySection()}
        </motion.div>

        <motion.div
          key="faq"
          initial={IS_PRERENDER ? false : { opacity: 0 }}
          animate={{ opacity: activeSection === "FAQ" ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: activeSection === "FAQ" ? 'block' : 'none' }}
        >
          {renderFAQSection()}
        </motion.div>

        <motion.div
          key="contact"
          initial={IS_PRERENDER ? false : { opacity: 0 }}
          animate={{ opacity: activeSection === "Nous contacter" ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: activeSection === "Nous contacter" ? 'block' : 'none' }}
        >
          <section id="demo-form" className="py-16 px-8 md:px-20 relative overflow-hidden" style={{ backgroundColor: '#FFFFFF', minHeight: '80vh' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={IS_PRERENDER ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className={`${heroTitleSize} font-bold text-center mb-8`} style={{ color: '#022444' }}>
              Formulaire de contact
            </h2>
          </motion.div>

          <motion.div
            initial={IS_PRERENDER ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <form
              onSubmit={handleSubmit}
              className="max-w-lg mx-auto space-y-5"
            >
            <input
            type="text"
            name="name"
            placeholder="Votre entreprise"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full p-3 rounded-xl bg-white border-2 focus:outline-none focus:ring-2 text-gray-900"
            style={{ borderColor: '#1A79B6', focusRingColor: '#022444' }}
          />
          <input
            type="email"
            name="email"
            placeholder="Votre e-mail"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full p-3 rounded-xl bg-white border-2 focus:outline-none focus:ring-2 text-gray-900"
            style={{ borderColor: '#1A79B6', focusRingColor: '#022444' }}
          />
          <textarea
            name="message"
            placeholder="Votre message"
            rows="4"
            value={formData.message}
            onChange={handleInputChange}
            required
            className="w-full p-3 rounded-xl bg-white border-2 focus:outline-none focus:ring-2 text-gray-900"
            style={{ borderColor: '#1A79B6', focusRingColor: '#022444' }}
          />
          
          {submitStatus === "success" && (
            <div className="p-4 bg-green-600 rounded-xl text-white text-center">
              Votre demande de démonstration a été envoyée avec succès ! Nous vous contacterons bientôt.
            </div>
          )}
          
          {submitStatus === "error" && (
            <div className="p-4 bg-red-600 rounded-xl text-white text-center">
              Une erreur est survenue.
            </div>
          )}
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-xl font-semibold text-lg transition-all text-white ${
              isSubmitting 
                ? "cursor-not-allowed opacity-50" 
                : "hover:opacity-90"
            }`}
            style={{ backgroundColor: isSubmitting ? '#1A79B6' : '#1A79B6' }}
          >
            {isSubmitting ? "Envoi en cours..." : "Envoyer"}
          </button>
          </form>
          </motion.div>
        </div>
        </section>
        </motion.div>

      {/* Formulaire visible aussi depuis "Notre système" */}
        <section id="demo-form-system" className="px-8 md:px-20 relative overflow-hidden" style={{ backgroundColor: '#FFFFFF', paddingTop: 0, paddingBottom: '50px', display: activeSection === "Notre système" ? 'block' : 'none' }}>
        <div className="max-w-lg mx-auto">
          {/* Formulaire */}
          <div>
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center" style={{ color: '#022444' }}>
              Nous contacter
            </h3>
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
          <input
            type="text"
            name="name"
            placeholder="Votre entreprise"
            value={formData.name}
            onChange={handleInputChange}
            required
            className="w-full p-3 rounded-xl bg-white border-2 focus:outline-none focus:ring-2 text-gray-900"
            style={{ borderColor: '#1A79B6', focusRingColor: '#022444' }}
          />
          <input
            type="email"
            name="email"
            placeholder="Votre e-mail"
            value={formData.email}
            onChange={handleInputChange}
            required
            className="w-full p-3 rounded-xl bg-white border-2 focus:outline-none focus:ring-2 text-gray-900"
            style={{ borderColor: '#1A79B6', focusRingColor: '#022444' }}
          />
          <textarea
            name="message"
            placeholder="Votre message"
            rows="4"
            value={formData.message}
            onChange={handleInputChange}
            required
            className="w-full p-3 rounded-xl bg-white border-2 focus:outline-none focus:ring-2 text-gray-900"
            style={{ borderColor: '#1A79B6', focusRingColor: '#022444' }}
          />
          
          {submitStatus === "success" && (
            <div className="p-4 bg-green-600 rounded-xl text-white text-center">
              Votre demande de démonstration a été envoyée avec succès ! Nous vous contacterons bientôt.
            </div>
          )}
          
          {submitStatus === "error" && (
            <div className="p-4 bg-red-600 rounded-xl text-white text-center">
              Une erreur est survenue.
            </div>
          )}
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-xl font-semibold text-lg transition-all text-white ${
              isSubmitting 
                ? "cursor-not-allowed opacity-50" 
                : "hover:opacity-90"
            }`}
            style={{ backgroundColor: isSubmitting ? '#1A79B6' : '#1A79B6' }}
          >
            {isSubmitting ? "Envoi en cours..." : "Envoyer"}
          </button>
        </form>
          </div>
        </div>
      </section>
      </div>

      <footer className="text-center py-8 text-sm" style={{ backgroundColor: '#F5F5F5', color: '#022444' }}>
        © {new Date().getFullYear()} | IAEGIS
      </footer>
    </div>
  );
}